import {
  MessageType,
  encodeChunkRequest,
  encodePlayerPosition,
  encodeBlockModify,
  encodeChatSend,
  encodeInventorySync,
  encodeWorldJoin,
  encodeWorldLeave,
  encodeWorldCreate,
  encodeWorldDelete,
  encodePortalActivate,
  encodeBedInteract,
  encodeTeleportBlockActivate,
  encodeTNTActivate,
  decodeMessage,
  decodeChunkData,
  decodePlayerPositions,
  decodeBlockUpdate,
  decodeWelcome,
  decodePlayerJoin,
  decodePlayerLeave,
  decodeChatBroadcast,
  decodeTimeSync,
  decodeNPCSpawn,
  decodeNPCPositions,
  decodeInventorySync,
  decodeWorldList,
  decodeWorldJoinResult,
  decodePortalTeleport,
  decodePortalSync,
  decodeSleepSync,
  decodeTeleportBlockResult,
  decodeTNTExplode,
  decodeTNTFuse,
  rleDecode,
  type NPCSpawnData,
  type NPCPosData,
  type PlayerAppearance,
  type InventoryItem,
  type WorldListEntry,
  type PortalSyncEntry,
} from '../protocol';
import { CHUNK_SIZE, CHUNK_HEIGHT } from '../world';

export interface NetworkCallbacks {
  onWelcome: (playerId: number, seed: number, spawnX: number, spawnY: number, spawnZ: number) => void;
  onChunkData: (cx: number, cz: number, blocks: Uint8Array, maxY: number) => void;
  onPlayerPositions: (positions: { id: number; x: number; y: number; z: number; yaw: number; pitch: number }[]) => void;
  onBlockUpdate: (wx: number, wy: number, wz: number, blockId: number) => void;
  onPlayerJoin: (id: number, name: string, appearance?: PlayerAppearance) => void;
  onPlayerLeave: (id: number) => void;
  onChatMessage: (name: string, message: string) => void;
  onTimeSync: (time: number) => void;
  onNPCSpawn: (npcs: NPCSpawnData[]) => void;
  onNPCPositions: (npcs: NPCPosData[]) => void;
  onInventorySync: (items: InventoryItem[]) => void;
  onWorldList: (worlds: WorldListEntry[]) => void;
  onWorldJoinResult: (success: boolean, reason: string) => void;
  onPortalTeleport: (x: number, y: number, z: number, yaw: number) => void;
  onPortalSync: (portals: PortalSyncEntry[]) => void;
  onSleepSync: (sleepingCount: number, totalCount: number, isSelfSleeping: boolean) => void;
  onTeleportBlockResult: (x: number, y: number, z: number, yaw: number) => void;
  onTNTExplode: (x: number, y: number, z: number) => void;
  onTNTFuse: (wx: number, wy: number, wz: number, duration: number) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnecting?: (delayMs: number, attempt: number) => void;
  onReconnected?: () => void;
}

export class NetworkClient {
  private ws: WebSocket | null = null;
  private callbacks: NetworkCallbacks;
  private positionInterval: ReturnType<typeof setInterval> | null = null;
  private pendingChunks = new Set<string>();
  private playerName: string;
  private playerUuid: string;
  private playerAppearance?: PlayerAppearance;
  private intentionalDisconnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private hasConnectedBefore = false;

  // Current player state to send
  playerX = 0;
  playerY = 0;
  playerZ = 0;
  playerYaw = 0;
  playerPitch = 0;

  connected = false;

  constructor(callbacks: NetworkCallbacks, name: string, uuid: string, appearance?: PlayerAppearance) {
    this.callbacks = callbacks;
    this.playerName = name;
    this.playerUuid = uuid;
    this.playerAppearance = appearance;
  }

  private getReconnectDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempt), 30000);
    return delay;
  }

  private scheduleReconnect() {
    if (this.intentionalDisconnect) return;
    const delay = this.getReconnectDelay();
    this.reconnectAttempt++;
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})...`);
    this.callbacks.onReconnecting?.(delay, this.reconnectAttempt);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  connect() {
    this.intentionalDisconnect = false;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const params = new URLSearchParams({ name: this.playerName, uuid: this.playerUuid });
    if (this.playerAppearance) {
      params.set('hair', this.playerAppearance.hairColor.toString(16).padStart(6, '0'));
      params.set('skin', this.playerAppearance.skinColor.toString(16).padStart(6, '0'));
      params.set('shirt', this.playerAppearance.shirtColor.toString(16).padStart(6, '0'));
      params.set('pants', this.playerAppearance.pantsColor.toString(16).padStart(6, '0'));
      params.set('shoes', this.playerAppearance.shoeColor.toString(16).padStart(6, '0'));
      params.set('model', String(this.playerAppearance.modelType ?? 0));
    }
    this.ws = new WebSocket(`${protocol}//${location.host}/ws?${params}`);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      const isReconnect = this.hasConnectedBefore;
      this.connected = true;
      this.hasConnectedBefore = true;
      this.reconnectAttempt = 0;
      this.callbacks.onConnect();
      if (isReconnect) {
        this.callbacks.onReconnected?.();
      }

      // Send position at ~10Hz
      this.positionInterval = setInterval(() => {
        this.sendPosition();
      }, 100);
    };

    this.ws.onmessage = (event) => {
      const data = event.data as ArrayBuffer;
      const { type, view, bytes } = decodeMessage(data);

      switch (type) {
        case MessageType.Welcome: {
          const w = decodeWelcome(view);
          this.callbacks.onWelcome(w.playerId, w.seed, w.spawnX, w.spawnY, w.spawnZ);
          break;
        }
        case MessageType.ChunkData: {
          const cd = decodeChunkData(view, bytes);
          const blockData = rleDecode(cd.compressedBlocks, CHUNK_SIZE * CHUNK_HEIGHT * CHUNK_SIZE);
          this.pendingChunks.delete(`${cd.cx},${cd.cz}`);
          this.callbacks.onChunkData(cd.cx, cd.cz, blockData, cd.maxY);
          break;
        }
        case MessageType.PlayerPositions: {
          const positions = decodePlayerPositions(view);
          this.callbacks.onPlayerPositions(positions);
          break;
        }
        case MessageType.BlockUpdate: {
          const bu = decodeBlockUpdate(view);
          this.callbacks.onBlockUpdate(bu.wx, bu.wy, bu.wz, bu.blockId);
          break;
        }
        case MessageType.PlayerJoin: {
          const pj = decodePlayerJoin(view, bytes);
          this.callbacks.onPlayerJoin(pj.id, pj.name, pj.appearance);
          break;
        }
        case MessageType.PlayerLeave: {
          const pl = decodePlayerLeave(view);
          this.callbacks.onPlayerLeave(pl.id);
          break;
        }
        case MessageType.ChatMessage: {
          const chat = decodeChatBroadcast(bytes);
          this.callbacks.onChatMessage(chat.name, chat.message);
          break;
        }
        case MessageType.TimeSync: {
          const ts = decodeTimeSync(view);
          this.callbacks.onTimeSync(ts.time);
          break;
        }
        case MessageType.NPCSpawn: {
          const npcs = decodeNPCSpawn(view);
          this.callbacks.onNPCSpawn(npcs);
          break;
        }
        case MessageType.NPCPositions: {
          const npcs = decodeNPCPositions(view);
          this.callbacks.onNPCPositions(npcs);
          break;
        }
        case MessageType.InventorySync: {
          const items = decodeInventorySync(view);
          this.callbacks.onInventorySync(items);
          break;
        }
        case MessageType.WorldList: {
          const worlds = decodeWorldList(bytes);
          this.callbacks.onWorldList(worlds);
          break;
        }
        case MessageType.WorldJoinResult: {
          const result = decodeWorldJoinResult(bytes);
          this.callbacks.onWorldJoinResult(result.success, result.reason);
          break;
        }
        case MessageType.PortalTeleport: {
          const pt = decodePortalTeleport(view);
          this.callbacks.onPortalTeleport(pt.x, pt.y, pt.z, pt.yaw);
          break;
        }
        case MessageType.PortalSync: {
          const portals = decodePortalSync(view);
          this.callbacks.onPortalSync(portals);
          break;
        }
        case MessageType.SleepSync: {
          const ss = decodeSleepSync(view);
          this.callbacks.onSleepSync(ss.sleepingCount, ss.totalCount, ss.isSelfSleeping);
          break;
        }
        case MessageType.TeleportBlockResult: {
          const tb = decodeTeleportBlockResult(view);
          this.callbacks.onTeleportBlockResult(tb.x, tb.y, tb.z, tb.yaw);
          break;
        }
        case MessageType.TNTExplode: {
          const te = decodeTNTExplode(view);
          this.callbacks.onTNTExplode(te.x, te.y, te.z);
          break;
        }
        case MessageType.TNTFuse: {
          const tf = decodeTNTFuse(view);
          this.callbacks.onTNTFuse(tf.wx, tf.wy, tf.wz, tf.duration);
          break;
        }
      }
    };

    this.ws.onerror = (event) => {
      console.error('WebSocket error', event);
    };

    this.ws.onclose = () => {
      this.connected = false;
      if (this.positionInterval) {
        clearInterval(this.positionInterval);
        this.positionInterval = null;
      }
      this.pendingChunks.clear();
      this.callbacks.onDisconnect();
      this.scheduleReconnect();
    };
  }

  requestChunk(cx: number, cz: number) {
    const key = `${cx},${cz}`;
    if (this.pendingChunks.has(key)) return;
    this.pendingChunks.add(key);
    this.send(encodeChunkRequest(cx, cz));
  }

  sendPosition() {
    if (!this.connected) return;
    this.send(encodePlayerPosition(
      this.playerX, this.playerY, this.playerZ,
      this.playerYaw, this.playerPitch,
    ));
  }

  sendBlockModify(wx: number, wy: number, wz: number, blockId: number) {
    this.send(encodeBlockModify(wx, wy, wz, blockId));
  }

  sendChatMessage(message: string) {
    this.send(encodeChatSend(message));
  }

  sendInventory(items: InventoryItem[]) {
    this.send(encodeInventorySync(items));
  }

  joinWorld(worldId: string, password?: string) {
    this.send(encodeWorldJoin(worldId, password));
  }

  leaveWorld() {
    this.pendingChunks.clear();
    this.send(encodeWorldLeave());
  }

  createWorld(name: string, seed?: string, password?: string) {
    this.send(encodeWorldCreate(name, seed, password));
  }

  deleteWorld(worldId: string) {
    this.send(encodeWorldDelete(worldId));
  }

  sendPortalActivate(wx: number, wy: number, wz: number) {
    this.send(encodePortalActivate(wx, wy, wz));
  }

  sendBedInteract(wx: number, wy: number, wz: number) {
    this.send(encodeBedInteract(wx, wy, wz));
  }

  sendTeleportBlockActivate(wx: number, wy: number, wz: number) {
    this.send(encodeTeleportBlockActivate(wx, wy, wz));
  }

  sendTNTActivate(wx: number, wy: number, wz: number) {
    this.send(encodeTNTActivate(wx, wy, wz));
  }

  private send(data: ArrayBuffer) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.positionInterval) {
      clearInterval(this.positionInterval);
      this.positionInterval = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
    this.pendingChunks.clear();
  }
}
